/**
 * PublicLeagueWebsite.js
 * Public-facing rendered league website.
 * Route: /league/:identifier (unauthenticated)
 * :identifier can be a subdomain slug or a leagueId UUID.
 *
 * Sections (rendered in configured order, only if enabled):
 *   schedule, standings, registration, rosters, news, photos, sponsors, about
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../services/api';
import { getSportConfig } from '../utils/sportConfig';
import { formatDate } from '../utils/dateFormat';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTextColor(hex) {
  // Return black or white text depending on background brightness
  if (!hex) return '#111827';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#111827' : '#ffffff';
}

// ─── Section: Schedule ────────────────────────────────────────────────────────

function ScheduleSection({ schedule, registration, theme }) {
  const accent = theme.primaryColor;

  if (!schedule || schedule.length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No games scheduled yet.</p>;
  }

  return (
    <div>
      {registration?.open && (
        <div style={{
          background: accent + '18',
          border: `1px solid ${accent}44`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontWeight: 600, color: accent }}>Registration is open!</span>
          <a
            href={`/register/${registration.formId}`}
            style={{
              background: accent,
              color: getTextColor(accent),
              padding: '8px 18px',
              borderRadius: 6,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Register Now →
          </a>
        </div>
      )}
      {schedule.map((week) => (
        <div key={week.week} style={{ marginBottom: 24 }}>
          <div style={{
            fontSize: '0.8rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#6b7280',
            marginBottom: 8,
          }}>
            Week {week.week}{week.date ? ` — ${formatDate(week.date)}` : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {week.games.map((game) => (
              <div key={game.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 8,
                background: '#f9fafb',
                flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: '0.8rem', color: '#9ca3af', minWidth: 44 }}>
                  {game.time || '—'}
                </span>
                <span style={{ fontWeight: 600, flex: 1, minWidth: 120 }}>
                  {game.team1} <span style={{ fontWeight: 400, color: '#9ca3af' }}>vs</span> {game.team2}
                </span>
                {game.played ? (
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: accent }}>
                    {game.team1Score} – {game.team2Score}
                    {game.forfeitTeam && <span style={{ fontSize: '0.75rem', color: '#ef4444', marginLeft: 6 }}>(forfeit)</span>}
                  </span>
                ) : game.status === 'cancelled' ? (
                  <span style={{ fontSize: '0.8rem', color: '#ef4444' }}>Cancelled</span>
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Upcoming</span>
                )}
                {game.venue && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', width: '100%', paddingLeft: 56 }}>
                    {game.venue.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Section: Standings ───────────────────────────────────────────────────────

function StandingsSection({ standings, sport, theme }) {
  const accent = theme.primaryColor;
  const config = getSportConfig(sport);
  const showGoals = ['soccer', 'hockey', 'softball', 'football'].includes(sport);

  if (!standings || standings.length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No standings yet.</p>;
  }

  const thStyle = {
    padding: '8px 12px',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: '#fff',
    background: accent,
    whiteSpace: 'nowrap',
  };
  const thNumStyle = { ...thStyle, textAlign: 'right' };
  const tdStyle = { padding: '9px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem' };
  const tdNumStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 32 }}>#</th>
            <th style={thStyle}>Team</th>
            <th style={thNumStyle}>W</th>
            <th style={thNumStyle}>L</th>
            {config.allowTies && <th style={thNumStyle}>T</th>}
            <th style={thNumStyle}>Pts</th>
            {showGoals && <th style={thNumStyle}>{config.statLabels.goalsFor}</th>}
            {showGoals && <th style={thNumStyle}>{config.statLabels.goalsAgainst}</th>}
          </tr>
        </thead>
        <tbody>
          {standings.map((team, idx) => (
            <tr key={team.id} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
              <td style={{ ...tdStyle, color: '#9ca3af', fontSize: '0.8rem' }}>{idx + 1}</td>
              <td style={{ ...tdStyle, fontWeight: 600 }}>{team.name}</td>
              <td style={tdNumStyle}>{team.wins}</td>
              <td style={tdNumStyle}>{team.losses}</td>
              {config.allowTies && <td style={tdNumStyle}>{team.ties}</td>}
              <td style={{ ...tdNumStyle, fontWeight: 700, color: accent }}>{team.points}</td>
              {showGoals && <td style={tdNumStyle}>{team.goalsFor ?? 0}</td>}
              {showGoals && <td style={tdNumStyle}>{team.goalsAgainst ?? 0}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Section: Registration ────────────────────────────────────────────────────

function RegistrationSection({ registration, theme }) {
  const accent = theme.primaryColor;

  if (!registration) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Registration is not available.</p>;
  }

  if (!registration.open) {
    return (
      <div style={{
        padding: '24px',
        borderRadius: 10,
        background: '#f3f4f6',
        textAlign: 'center',
        color: '#6b7280',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔒</div>
        <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 4 }}>Registration is closed</div>
        {registration.deadline && (
          <div style={{ fontSize: '0.9rem' }}>
            Deadline was {formatDate(registration.deadline)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      padding: '28px',
      borderRadius: 10,
      background: accent + '10',
      border: `1px solid ${accent}33`,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 8, color: accent }}>
        Registration is Open!
      </div>
      {registration.fee > 0 && (
        <div style={{ fontSize: '1rem', marginBottom: 4 }}>
          Registration fee: <strong>${(registration.fee / 100).toFixed(2)}</strong>
        </div>
      )}
      {registration.deadline && (
        <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 16 }}>
          Deadline: {formatDate(registration.deadline)}
        </div>
      )}
      <a
        href={`/register/${registration.formId}`}
        style={{
          display: 'inline-block',
          background: accent,
          color: getTextColor(accent),
          padding: '12px 32px',
          borderRadius: 8,
          textDecoration: 'none',
          fontWeight: 700,
          fontSize: '1rem',
        }}
      >
        Register Now →
      </a>
    </div>
  );
}

// ─── Section: Rosters ─────────────────────────────────────────────────────────

function RostersSection({ rosters, theme }) {
  const accent = theme.primaryColor;
  const [expanded, setExpanded] = useState({});

  if (!rosters || rosters.length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No roster data available.</p>;
  }

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
      {rosters.map((team) => (
        <div key={team.id} style={{
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          <button
            onClick={() => toggle(team.id)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: '12px 16px',
              background: expanded[team.id] ? accent : '#f9fafb',
              color: expanded[team.id] ? getTextColor(accent) : '#111827',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              textAlign: 'left',
            }}
          >
            <span>{team.name}</span>
            <span style={{ fontSize: '0.8rem' }}>
              {team.players.length} player{team.players.length !== 1 ? 's' : ''} {expanded[team.id] ? '▲' : '▼'}
            </span>
          </button>
          {expanded[team.id] && (
            <div style={{ padding: '8px 0' }}>
              {team.players.length === 0 ? (
                <div style={{ padding: '8px 16px', color: '#9ca3af', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  No players listed
                </div>
              ) : (
                team.players.map((p) => (
                  <div key={p.id} style={{
                    padding: '6px 16px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f3f4f6',
                  }}>
                    <span>{p.name}</span>
                    {p.role && p.role !== 'player' && (
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'capitalize' }}>{p.role}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Section: News ────────────────────────────────────────────────────────────

function NewsSection({ announcements, theme }) {
  const accent = theme.primaryColor;

  if (!announcements || announcements.length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No announcements yet.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {announcements.map((ann) => (
        <div key={ann.id} style={{
          padding: '16px 20px',
          borderRadius: 10,
          border: `1px solid ${ann.pinned ? accent + '44' : '#e5e7eb'}`,
          background: ann.pinned ? accent + '08' : '#ffffff',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>{ann.title}</div>
            {ann.pinned && (
              <span style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: accent,
                border: `1px solid ${accent}`,
                borderRadius: 4,
                padding: '2px 6px',
                whiteSpace: 'nowrap',
              }}>
                Pinned
              </span>
            )}
          </div>
          <div style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {ann.content}
          </div>
          {ann.publishedAt && (
            <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 8 }}>
              {formatDate(ann.publishedAt)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Section: Sponsors ────────────────────────────────────────────────────────

function SponsorsSection({ sponsors }) {
  if (!sponsors || (sponsors.gold.length + sponsors.silver.length + sponsors.bronze.length) === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No sponsors listed.</p>;
  }

  const TierGroup = ({ title, items, size }) => {
    if (!items || items.length === 0) return null;
    const logoSize = size === 'large' ? 120 : size === 'medium' ? 80 : 60;

    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: '#9ca3af',
          marginBottom: 12,
        }}>
          {title}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          {items.map((s) => {
            const inner = (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#fafafa',
                minWidth: logoSize + 24,
              }}>
                {s.logoUrl ? (
                  <img
                    src={s.logoUrl}
                    alt={s.name}
                    style={{ width: logoSize, height: logoSize * 0.6, objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: logoSize,
                    height: logoSize * 0.6,
                    background: '#f3f4f6',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                  }}>
                    {s.name}
                  </div>
                )}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', textAlign: 'center' }}>
                  {s.name}
                </span>
              </div>
            );
            const safeUrl = s.websiteUrl && /^https?:\/\//i.test(s.websiteUrl) ? s.websiteUrl : null;
            return safeUrl ? (
              <a key={s.id} href={safeUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                {inner}
              </a>
            ) : (
              <div key={s.id}>{inner}</div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <TierGroup title="Gold Sponsors" items={sponsors.gold} size="large" />
      <TierGroup title="Silver Sponsors" items={sponsors.silver} size="medium" />
      <TierGroup title="Bronze Sponsors" items={sponsors.bronze} size="small" />
    </div>
  );
}

// ─── Section: About ───────────────────────────────────────────────────────────

function AboutSection({ content }) {
  if (!content) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No about info provided.</p>;
  }
  return (
    <div style={{ lineHeight: 1.7, fontSize: '1rem', color: '#374151', whiteSpace: 'pre-line' }}>
      {content}
    </div>
  );
}

// ─── Section: Photos ─────────────────────────────────────────────────────────

function PhotosSection({ photos, theme }) {
  const [lightbox, setLightbox] = useState(null); // index of open photo

  if (!photos || photos.length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No photos yet.</p>;
  }

  const openPrev = () => setLightbox(i => (i > 0 ? i - 1 : photos.length - 1));
  const openNext = () => setLightbox(i => (i < photos.length - 1 ? i + 1 : 0));

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') openPrev();
    if (e.key === 'ArrowRight') openNext();
    if (e.key === 'Escape') setLightbox(null);
  };

  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 12,
      }}>
        {photos.map((photo, idx) => (
          <div
            key={photo.id}
            onClick={() => setLightbox(idx)}
            style={{
              cursor: 'pointer',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              background: '#f3f4f6',
              aspectRatio: '4/3',
              position: 'relative',
            }}
          >
            <img
              src={photo.imageUrl}
              alt={photo.caption || `Photo ${idx + 1}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => { e.target.style.transform = 'scale(1.03)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          onClick={() => setLightbox(null)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          {/* Prev */}
          {photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); openPrev(); }}
              style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                width: 44, height: 44, borderRadius: '50%', fontSize: '1.2rem',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ‹
            </button>
          )}

          {/* Image */}
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', textAlign: 'center' }}>
            <img
              src={photos[lightbox].imageUrl}
              alt={photos[lightbox].caption || `Photo ${lightbox + 1}`}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
            />
            {photos[lightbox].caption && (
              <div style={{ color: '#f9fafb', marginTop: 10, fontSize: '0.95rem', maxWidth: 600, margin: '10px auto 0' }}>
                {photos[lightbox].caption}
              </div>
            )}
            <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: 6 }}>
              {lightbox + 1} / {photos.length}
            </div>
          </div>

          {/* Next */}
          {photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); openNext(); }}
              style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                width: 44, height: 44, borderRadius: '50%', fontSize: '1.2rem',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ›
            </button>
          )}

          {/* Close */}
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              width: 36, height: 36, borderRadius: '50%', fontSize: '1rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}

// ─── Section Renderer ─────────────────────────────────────────────────────────

const SECTION_LABELS = {
  schedule:     'Schedule',
  standings:    'Standings',
  registration: 'Registration',
  rosters:      'Rosters',
  news:         'News',
  photos:       'Photos',
  sponsors:     'Sponsors',
  about:        'About',
};

function Section({ id, label, theme, children }) {
  const accent = theme.primaryColor;
  return (
    <section id={id} style={{
      padding: '40px 0',
      borderBottom: '1px solid #f0f0f0',
    }}>
      <h2 style={{
        fontSize: '1.4rem',
        fontWeight: 700,
        color: accent,
        marginBottom: 20,
        paddingBottom: 10,
        borderBottom: `2px solid ${accent}22`,
      }}>
        {label}
      </h2>
      {children}
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PublicLeagueWebsite({ hostname }) {
  const { identifier } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    const fetch = hostname
      ? api.getPublicWebsiteByHostname(hostname)
      : api.getPublicWebsite(identifier);
    fetch
      .then((res) => { setData(res); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [identifier, hostname]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f9fafb',
        color: '#6b7280',
        fontSize: '1.1rem',
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f9fafb',
        gap: 12,
      }}>
        <div style={{ fontSize: '3rem' }}>🏟️</div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#374151' }}>League Not Found</h1>
        <p style={{ color: '#6b7280' }}>{error}</p>
        <a href="https://leaguemaker.app" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
          Create your own league →
        </a>
      </div>
    );
  }

  const { website, league, schedule, standings, registration, rosters, announcements, sponsors, photos } = data;
  const theme = website.theme || {};
  const isValidColor = (c) => /^#[0-9A-Fa-f]{3,8}$/.test(c);
  const primaryColor = isValidColor(theme.primaryColor) ? theme.primaryColor : '#2563eb';
  const secondaryColor = isValidColor(theme.secondaryColor) ? theme.secondaryColor : '#1e40af';
  const backgroundColor = isValidColor(theme.backgroundColor) ? theme.backgroundColor : '#ffffff';
  const textColor = isValidColor(theme.textColor) ? theme.textColor : '#111827';

  const sections = website.sections || {};
  const enabledSections = Object.entries(sections)
    .filter(([, cfg]) => cfg.enabled)
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key]) => key);

  const navOnPrimary = getTextColor(primaryColor);

  // ── Header ──

  const header = (
    <header>
      {/* Banner */}
      {theme.bannerUrl && /^https?:\/\//i.test(theme.bannerUrl) && (
        <div style={{
          width: '100%',
          height: 200,
          backgroundImage: `url(${theme.bannerUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} />
      )}

      {/* Title bar */}
      <div style={{
        background: primaryColor,
        color: navOnPrimary,
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}>
        {theme.logoUrl && /^https?:\/\//i.test(theme.logoUrl) && (
          <img
            src={theme.logoUrl}
            alt="league logo"
            style={{ height: 44, width: 44, objectFit: 'contain', borderRadius: 6 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.2 }}>{league.name}</div>
          {league.seasonYear && (
            <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{league.seasonYear} Season</div>
          )}
        </div>
      </div>

      {/* Nav bar */}
      {enabledSections.length > 0 && (
        <nav style={{ background: secondaryColor }}>
          {/* Desktop nav */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0,
            maxWidth: 900,
            margin: '0 auto',
            padding: '0 16px',
          }}>
            {enabledSections.map((key) => (
              <a
                key={key}
                href={`#section-${key}`}
                style={{
                  color: 'rgba(255,255,255,0.88)',
                  textDecoration: 'none',
                  padding: '10px 14px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  display: 'inline-block',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => { e.target.style.color = '#ffffff'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(255,255,255,0.88)'; }}
              >
                {SECTION_LABELS[key]}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );

  // ── Sections ──

  const renderSection = (key) => {
    switch (key) {
      case 'schedule':
        return (
          <Section key={key} id="section-schedule" label="Schedule" theme={{ primaryColor }}>
            <ScheduleSection schedule={schedule} registration={registration} theme={{ primaryColor }} />
          </Section>
        );
      case 'standings':
        return (
          <Section key={key} id="section-standings" label="Standings" theme={{ primaryColor }}>
            <StandingsSection standings={standings} sport={league.sport} theme={{ primaryColor }} />
          </Section>
        );
      case 'registration':
        return (
          <Section key={key} id="section-registration" label="Registration" theme={{ primaryColor }}>
            <RegistrationSection registration={registration} theme={{ primaryColor }} />
          </Section>
        );
      case 'rosters':
        return (
          <Section key={key} id="section-rosters" label="Rosters" theme={{ primaryColor }}>
            <RostersSection rosters={rosters} theme={{ primaryColor }} />
          </Section>
        );
      case 'news':
        return (
          <Section key={key} id="section-news" label="News" theme={{ primaryColor }}>
            <NewsSection announcements={announcements} theme={{ primaryColor }} />
          </Section>
        );
      case 'photos':
        return (
          <Section key={key} id="section-photos" label="Photos" theme={{ primaryColor }}>
            <PhotosSection photos={photos} theme={{ primaryColor }} />
          </Section>
        );
      case 'sponsors':
        return (
          <Section key={key} id="section-sponsors" label="Sponsors" theme={{ primaryColor }}>
            <SponsorsSection sponsors={sponsors} />
          </Section>
        );
      case 'about':
        return (
          <Section key={key} id="section-about" label="About" theme={{ primaryColor }}>
            <AboutSection content={website.aboutContent} />
          </Section>
        );
      default:
        return null;
    }
  };

  // ── Footer ──

  const footer = (
    <footer style={{
      background: '#111827',
      color: '#9ca3af',
      padding: '24px',
      textAlign: 'center',
      fontSize: '0.85rem',
      marginTop: 40,
    }}>
      {league.description && (
        <p style={{ maxWidth: 600, margin: '0 auto 12px', color: '#d1d5db' }}>{league.description}</p>
      )}
      <div>
        Powered by{' '}
        <a
          href="https://leaguemaker.app"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#60a5fa', textDecoration: 'none', fontWeight: 600 }}
        >
          LeagueMaker
        </a>
        {' '}— Run your league online.
      </div>
    </footer>
  );

  return (
    <div style={{ minHeight: '100vh', background: backgroundColor, color: textColor, fontFamily: 'system-ui, sans-serif' }}>
      {header}
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px' }}>
        {enabledSections.map(renderSection)}
        {enabledSections.length === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🏟️</div>
            <p>No sections are enabled on this league website yet.</p>
          </div>
        )}
      </main>
      {footer}
    </div>
  );
}
